import { BaseUITools } from "@/uiControls/base/baseUITools";
import { modelUtils } from "@/utils/modelUtils";
import { createNumericSliderUI, NumericSliderUI } from "@/uiControls/base/ui/numericSliderUI";
import { NumericInputUI } from "../base/ui/numericInputUI";
import { createSwitchUI, SwitchUI } from "@/uiControls/base/ui/switchUI";
import { createVector3ControlUI, Vector3ControlUI } from "../base/components/vector3ControlUI";
import { globalConfig, globalManager } from "@/utils/globalConfig";
import { getI18nText } from "@/utils/commonFunc";

// 模型辅助工具，扩展多视窗，与Scene无绑定关系，单独使用
export class ModelPanelTools extends BaseUITools {

    private isActive: boolean = false; // 是否激活
    private modelPanelMode: 'model' | 'recordingCamera' = 'model'; // 模型面板模式
    private panelId: string; // 面板ID
    private posControlDom: Vector3ControlUI;
    private rotationControlDom: Vector3ControlUI;
    private scaleControlDom: Vector3ControlUI;
    private gaussianSizeControlDom: NumericInputUI;
    private gaussianOpacityControlDom: NumericInputUI;
    private sphericalLevelControlDom: NumericSliderUI;
    private timeScaleControlDom: NumericInputUI;
    private timeOffsetControlDom: NumericInputUI;
    private maxPixelRadiusControlDom: NumericSliderUI;
    private loopControlDom: SwitchUI;

    // 标题元素
    private titledomElement: HTMLElement;
    // 录制相机参数
    private fovControlDom: NumericSliderUI;
    private nearPlaneControlDom: NumericInputUI;
    private farPlaneControlDom: NumericInputUI;

    private recordingCameraViewElement: HTMLElement;
    private gaussianViewElement: HTMLElement;

    // 事件监听器引用，用于在dispose时移除
    private modelSelectChangeHandler?: (event: Event) => void;
    private gizmoObjectChangeHandler?: (event: Event) => void;
    private panelTabChangeHandler?: (event: Event) => void;
    private modelParamsChangeHandler?: (event: Event) => void;

    constructor(id: string) {
        super(id, false);
        this.panelId = id;
        this.init();
        this.setActive(globalManager.getActivePanelTab() === 'model');
        this.setupEventHandlers();
        this.update();
    }

    init() {
        const panelContainerElement = this.ui.createElement({
            tag: 'div',
            className: 'panel-setting-container',
        });
        const titleElement = this.ui.createElement({
            tag: 'div',
            className: 'panel-setting-title',
        });
        const titleLeftElement = this.ui.createElement({
            tag: 'div',
            textContent: '模型设置',
            className: 'panel-setting-title-left',
            i18nKey: 'title.modelSettings'
        });
        this.titledomElement = titleLeftElement;
        const titleRightElement = this.ui.createElement({
            tag: 'div',
            className: 'panel-setting-title-right',
        });
        const resetButtonElement = this.ui.createButton({
            textContent: '重置',
            className: ['btn-item', 'btn-text-item', 'inherit-font-size'],
            i18nKey: 'buttons.reset'
        })
        resetButtonElement.addEventListener('click', () => {
            this.onReset();
        });
        titleRightElement.appendChild(resetButtonElement);

        const closeButtonElement = this.ui.createButton({
            iconName: 'cuowu1',
            isIconOnly: true,
            className: ['btn-item', 'btn-icon-item', 'inherit-font-size'],
        })
        closeButtonElement.addEventListener('click', () => {
            this.onClose();
        });
        titleRightElement.appendChild(closeButtonElement);

        titleElement.appendChild(titleLeftElement);
        titleElement.appendChild(titleRightElement);
        panelContainerElement.appendChild(titleElement);

        const viewElement = this.initViewSettingControl();
        panelContainerElement.appendChild(viewElement);

        const modelControlElement = this.initModelControl();
        this.gaussianViewElement = modelControlElement;
        panelContainerElement.appendChild(modelControlElement);

        const recordingCameraControlElement = this.initRecordingCameraControl();
        this.recordingCameraViewElement = recordingCameraControlElement;
        panelContainerElement.appendChild(recordingCameraControlElement);

        this.appendChild('panelContainer', panelContainerElement);
    }
    initViewSettingControl() {
        const viewElement = this.ui.createElement({
            tag: 'div',
            className: 'panel-setting-view',
        });
        const viewTitleElement = this.ui.createElement({
            tag: 'div',
            textContent: '位置设置',
            className: 'panel-setting-view-title',
            i18nKey: 'model.positionSetting'
        });
        viewElement.appendChild(viewTitleElement);

        const posControlElement = new createVector3ControlUI({
            label: '位置',
            containerClass: 'panel-setting-view',
            valueOptions: new Map([
                ['x', { id: 'posX', label: 'X', value: 0, step: 0.1, precision: -1, disabled: false, readonly: false, showValue: false, valueFormat: (value: number) => value.toFixed(2) }],
                ['y', { id: 'posY', label: 'Y', value: 0, step: 0.1, precision: -1, disabled: false, readonly: false, showValue: false, valueFormat: (value: number) => value.toFixed(2) }],
                ['z', { id: 'posZ', label: 'Z', value: 0, step: 0.1, precision: -1, disabled: false, readonly: false, showValue: false, valueFormat: (value: number) => value.toFixed(2) }],
            ]),
            isLabelInline: true,
            i18nKey: 'label.position'
        });
        this.posControlDom = posControlElement;
        this.posControlDom.onChange((value) => {
            modelUtils.onPositionChange(value);
        });
        viewElement.appendChild(posControlElement.getContainer());

        const rotationControlElement = new createVector3ControlUI({
            label: '旋转',
            containerClass: 'panel-setting-view',
            valueOptions: new Map([
                ['x', { id: 'rotationX', label: 'X', value: 0, step: 0.1, precision: -1, disabled: false, readonly: false, showValue: false, valueFormat: (value: number) => value.toFixed(2) }],
                ['y', { id: 'rotationY', label: 'Y', value: 0, step: 0.1, precision: -1, disabled: false, readonly: false, showValue: false, valueFormat: (value: number) => value.toFixed(2) }],
                ['z', { id: 'rotationZ', label: 'Z', value: 0, step: 0.1, precision: -1, disabled: false, readonly: false, showValue: false, valueFormat: (value: number) => value.toFixed(2) }],
            ]),
            isLabelInline: true,
            i18nKey: 'label.rotation'
        });
        this.rotationControlDom = rotationControlElement;
        this.rotationControlDom.onChange((value) => {
            modelUtils.onRotationChange(value);
        });
        viewElement.appendChild(rotationControlElement.getContainer());

        const scaleControlElement = new createVector3ControlUI({
            label: '缩放',
            containerClass: 'panel-setting-view',
            valueOptions: new Map([
                ['x', { id: 'scaleX', label: 'X', value: 1, step: 0.1, precision: -1, disabled: false, readonly: false, showValue: false, valueFormat: (value: number) => value.toFixed(2) }],
                ['y', { id: 'scaleY', label: 'Y', value: 1, step: 0.1, precision: -1, disabled: false, readonly: false, showValue: false, valueFormat: (value: number) => value.toFixed(2) }],
                ['z', { id: 'scaleZ', label: 'Z', value: 1, step: 0.1, precision: -1, disabled: false, readonly: false, showValue: false, valueFormat: (value: number) => value.toFixed(2) }],
            ]),
            isLabelInline: true,
            i18nKey: 'label.scale'
        });
        this.scaleControlDom = scaleControlElement;
        this.scaleControlDom.onChange((value) => {
            modelUtils.onScaleChange(value);
        });
        viewElement.appendChild(scaleControlElement.getContainer());

        return viewElement;
    }
    // 初始化高斯模型参数
    initModelControl() {
        const viewElement = this.ui.createElement({
            tag: 'div',
            className: 'panel-setting-view',
        });
        const viewTitleElement = this.ui.createElement({
            tag: 'div',
            textContent: '模型参数',
            className: 'panel-setting-view-title',
            i18nKey: 'label.modelParameters'
        });
        viewElement.appendChild(viewTitleElement);

        const gaussianSizeElement = new NumericInputUI({
            label: '高斯大小倍数',
            isLabelInline: false,
            value: globalConfig.modelParams.gaussianSize,
            min: null,
            max: null,
            step: 0.1,
            precision: 1,
            showValue: false,
            containerClass: ['panel-setting-item'],
            labelClass: 'panel-setting-item-label',
            i18nKey: 'model.modelGaussianSize'
        });
        this.gaussianSizeControlDom = gaussianSizeElement;

        this.gaussianSizeControlDom.onChange((value) => {
            modelUtils.onGaussianSizeChange(value);
        });
        viewElement.appendChild(gaussianSizeElement.getContainer());

        const gaussianOpacityElement = new NumericInputUI({
            label: '高斯透明度倍数',
            isLabelInline: false,
            value: globalConfig.modelParams.gaussianOpacity,
            min: null,
            max: null,
            step: 0.1,
            precision: 1,
            showValue: false,
            containerClass: ['panel-setting-item'],
            labelClass: 'panel-setting-item-label',
            i18nKey: 'model.modelGaussianOpacity'
        });
        this.gaussianOpacityControlDom = gaussianOpacityElement;
        this.gaussianOpacityControlDom.onChange((value) => {
            modelUtils.onGaussianOpacityChange(value);
        });
        viewElement.appendChild(gaussianOpacityElement.getContainer());

        const sphericalLevelElement = new createNumericSliderUI({
            id: 'spherical-level-control',
            containerClass: 'panel-setting-item',
            label: '球谐函数等级',
            showValue: false,
            value: globalConfig.modelParams.sphericalLevel,
            min: 0,
            max: 3,
            step: 1,
            precision: 0,
            labelClass: 'panel-setting-item-label',
            sliderContainerClass: 'panel-setting-item-content',
            i18nKey: 'model.modelMaxShDeg'
        });
        this.sphericalLevelControlDom = sphericalLevelElement;
        this.sphericalLevelControlDom.onChange((value) => {
            modelUtils.onSphericalLevelChange(value);
        });
        viewElement.appendChild(sphericalLevelElement.getContainer());


        const timeScaleElement = new NumericInputUI({
            label: '时间缩放倍数',
            isLabelInline: false,
            value: globalConfig.modelParams.timeScale,
            min: 0.01,
            max: null,
            step: 0.01,
            precision: 5,
            showValue: false,
            containerClass: ['panel-setting-item'],
            labelClass: 'panel-setting-item-label',
            i18nKey: 'model.modelTimeScale'
        });
        this.timeScaleControlDom = timeScaleElement;
        this.timeScaleControlDom.onChange((value) => {
            // console.log('timeScaleControlDom onChange', value);
            modelUtils.onTimeScaleChange(value);
        });
        viewElement.appendChild(timeScaleElement.getContainer());


        const timeOffsetElement = new NumericInputUI({
            label: '时间偏移',
            isLabelInline: false,
            value: globalConfig.modelParams.timeOffset,
            min: null,
            max: null,
            step: 1,
            precision: 4,
            showValue: false,
            containerClass: ['panel-setting-item'],
            labelClass: 'panel-setting-item-label',
            i18nKey: 'model.modelTimeOffset'
        });
        this.timeOffsetControlDom = timeOffsetElement;
        this.timeOffsetControlDom.onChange((value) => {
            // console.log('timeOffsetControlDom onChange', value);
            modelUtils.onTimeOffsetChange(value);
        });
        viewElement.appendChild(timeOffsetElement.getContainer());

        const loopControlElement = new createSwitchUI({
            id: 'loop-control',
            containerClass: 'panel-setting-item',
            label: '循环播放',
            checked: false,
            labelClass: 'panel-setting-item-label',
            switchContainerClass: 'panel-setting-item-content',
            i18nKey: 'model.modelLoop'
        });
        viewElement.appendChild(loopControlElement.getContainer());
        this.loopControlDom = loopControlElement;
        this.loopControlDom.onChange((value) => {
            // console.log('loopControlDom onChange', value);
            modelUtils.onLoopChange(value);
        });
        return viewElement;
    }
    // 初始化录制相机参数
    initRecordingCameraControl() {
        const viewElement = this.ui.createElement({
            tag: 'div',
            className: 'panel-setting-view',
        });
        const viewTitleElement = this.ui.createElement({
            tag: 'div',
            textContent: '录制相机参数',
            className: 'panel-setting-view-title',
            i18nKey: 'label.recordingCameraParameters'
        });

        viewElement.appendChild(viewTitleElement);

        const fovElement = createNumericSliderUI({
            id: 'fov-control',
            containerClass: 'panel-setting-item',
            value: globalConfig.cameraParams.fov,
            min: 10,
            max: 170,
            step: 1,
            label: '视野角',
            showValue: false,
            labelClass: 'panel-setting-item-label',
            i18nKey: 'label.fov'
        });
        this.fovControlDom = fovElement;

        // 添加FOV变化监听器
        fovElement.onChange((value: number | null) => {
            if (value !== null) {
                modelUtils.onRecordingCameraParamsChange('fov', value);
            }
        });

        viewElement.appendChild(fovElement.getContainer());

        const nearPlaneElement = new NumericInputUI({
            label: '近平面',
            isLabelInline: false,
            value: globalConfig.cameraParams.nearPlane,
            min: 0.01,
            max: 10,
            step: 0.01,
            showValue: false,
            containerClass: ['panel-setting-item'],
            labelClass: 'panel-setting-item-label',
            i18nKey: 'label.nearPlane'
        });
        this.nearPlaneControlDom = nearPlaneElement;

        // 添加近平面变化监听器
        nearPlaneElement.onChange((value: number | null) => {
            if (value !== null) {
                modelUtils.onRecordingCameraParamsChange('near', value);
            }
        });

        viewElement.appendChild(nearPlaneElement.getContainer());


        const farPlaneElement = new NumericInputUI({
            label: '远平面',
            isLabelInline: false,
            value: globalConfig.cameraParams.farPlane,
            min: 10,
            max: 10000,
            step: 10,
            showValue: false,
            containerClass: ['panel-setting-item'],
            labelClass: 'panel-setting-item-label',
            i18nKey: 'label.farPlane'
        });
        this.farPlaneControlDom = farPlaneElement;

        // 添加远平面变化监听器
        farPlaneElement.onChange((value: number | null) => {
            if (value !== null) {
                modelUtils.onRecordingCameraParamsChange('far', value);
            }
        });

        viewElement.appendChild(farPlaneElement.getContainer());
        return viewElement;
    }
    setupEventHandlers() {
        // 保存事件监听器引用，以便在dispose时移除
        this.modelSelectChangeHandler = (event: Event) => {
            const detail = (event as CustomEvent).detail;
            // console.log('detail', detail);
            this.update()
        };
        window.addEventListener('event.modelSelectChange', this.modelSelectChangeHandler);

        this.gizmoObjectChangeHandler = (event: Event) => {
            this.update()
            const detail = (event as CustomEvent).detail;
            modelUtils.onRecordingCameraChange(detail);
        };
        window.addEventListener('event.gizmoObjectChange', this.gizmoObjectChangeHandler);

        this.panelTabChangeHandler = (event: Event) => {
            // console.log('panelTabChange', event);
            const detail = (event as CustomEvent).detail;
            this.setActive(detail === 'model');
            this.update();
        };
        window.addEventListener('event.panelTabChange', this.panelTabChangeHandler);

        this.modelParamsChangeHandler = (event: Event) => {
            const detail = (event as CustomEvent).detail;
            const currentSelected = globalManager.getCurrentSelected();
            if (currentSelected.sceneid === detail.sceneid && currentSelected.objectid === detail.modelid) {
                this.update()
            }
        };
        window.addEventListener('event.modelParamsChange', this.modelParamsChangeHandler);
    }
    onReset() {
        console.log('重置');
        this.posControlDom.syncFromInput({ x: globalConfig.modelParams.position.x, y: globalConfig.modelParams.position.y, z: globalConfig.modelParams.position.z });
        this.rotationControlDom.syncFromInput({ x: globalConfig.modelParams.rotation.x, y: globalConfig.modelParams.rotation.y, z: globalConfig.modelParams.rotation.z });
        this.scaleControlDom.syncFromInput({ x: globalConfig.modelParams.scale.x, y: globalConfig.modelParams.scale.y, z: globalConfig.modelParams.scale.z });
        this.gaussianSizeControlDom.syncFromInput(globalConfig.modelParams.gaussianSize);
        this.gaussianOpacityControlDom.syncFromInput(globalConfig.modelParams.gaussianOpacity);
        this.sphericalLevelControlDom.syncFromInput(globalConfig.modelParams.maxShDeg);
        this.timeScaleControlDom.syncFromInput(globalConfig.modelParams.timeScale);
        this.timeOffsetControlDom.syncFromInput(globalConfig.modelParams.timeOffset);
        // this.maxPixelRadiusControlDom.setValue(globalConfig.modelParams.maxPixelRadius);
        this.loopControlDom.setChecked(globalConfig.modelParams.isLoop);

        if (this.modelPanelMode === 'recordingCamera') { // 录制相机模式下，重置录制相机参数 
            this.posControlDom.syncFromInput({ x: globalConfig.cameraParams.position.x, y: globalConfig.cameraParams.position.y, z: globalConfig.cameraParams.position.z });
            this.rotationControlDom.syncFromInput({ x: globalConfig.cameraParams.rotation.x, y: globalConfig.cameraParams.rotation.y, z: globalConfig.cameraParams.rotation.z });
            this.scaleControlDom.syncFromInput({ x: globalConfig.cameraParams.scale.x, y: globalConfig.cameraParams.scale.y, z: globalConfig.cameraParams.scale.z });
            this.fovControlDom.syncFromInput(globalConfig.recordingCameraParams.fov);
            this.nearPlaneControlDom.syncFromInput(globalConfig.recordingCameraParams.nearPlane);
            this.farPlaneControlDom.syncFromInput(globalConfig.recordingCameraParams.farPlane);
        }
    }
    onClose() {
        globalManager.closeRightToolsBar();
    }
    update() {
        if (this.isActive) {
        // console.log("scene panel tools update", this.isActive);
            const targetObject = globalManager.getCurrentObject();

            if (targetObject && targetObject.object) {
                let object = targetObject.object;
                this.posControlDom.setDisabled(false);
                this.posControlDom.setValue({ x: object.position.x, y: object.position.y, z: object.position.z });
                this.rotationControlDom.setDisabled(false);
                this.rotationControlDom.setValue({ x: object.rotation.x, y: object.rotation.y, z: object.rotation.z });
                this.scaleControlDom.setDisabled(false);
                this.scaleControlDom.setValue({ x: object.scale.x, y: object.scale.y, z: object.scale.z });

                const scene = globalManager.getCurrentSceneView();
                if (targetObject.type === 'visionary') {
                    this.setGaussianDomsDisabled(false);
                    this.setRecordingCameraDomsDisabled(true);

                    if (scene) {
                        const modelParams = scene.getModelGaussianParams(object.uuid);
                        // console.log('获取高斯模型所有参数', modelParams);
                        if (modelParams) {
                            this.gaussianSizeControlDom.setValue(modelParams.gaussianScale);
                            this.gaussianOpacityControlDom.setValue(modelParams.opacityScale);
                            this.sphericalLevelControlDom.setValue(modelParams.maxShDeg);
                            this.timeScaleControlDom.setValue(modelParams.timeScale);
                            this.timeOffsetControlDom.setValue(modelParams.timeOffset);
                            // this.maxPixelRadiusControlDom.setValue(modelParams.maxPixelRadius);
                            this.loopControlDom.setChecked(targetObject.isLoop);
                            this.setGaussianDomsDisabled(false);
                        } else { // 如果没有高斯参数设置dom disable
                            this.setGaussianDomsDisabled(true);
                        }
                    }
                } else {
                    this.setGaussianDomsDisabled(true);
                    if (targetObject.type === 'recordingCamera') {
                        this.setRecordingCameraDomsDisabled(false);
                        const recordingCameraParams = scene.getRecordingCameraById(object.uuid);
                        if (recordingCameraParams && recordingCameraParams.camera) {
                            this.fovControlDom.setValue(recordingCameraParams.camera.fov);
                            this.nearPlaneControlDom.setValue(recordingCameraParams.camera.near);
                            this.farPlaneControlDom.setValue(recordingCameraParams.camera.far);
                        }
                    } else {
                        this.setRecordingCameraDomsDisabled(true);
                    }
                }
            } else {
                this.posControlDom.setDisabled(true);
                this.rotationControlDom.setDisabled(true);
                this.scaleControlDom.setDisabled(true);
                this.setGaussianDomsDisabled(true);
                this.setRecordingCameraDomsDisabled(true);
            }
        }
    }

    setGaussianDomsDisabled(disabled: boolean) {
        this.gaussianSizeControlDom.setDisabled(disabled);
        this.gaussianOpacityControlDom.setDisabled(disabled);
        this.sphericalLevelControlDom.setDisabled(disabled);
        this.timeScaleControlDom.setDisabled(disabled);
        this.timeOffsetControlDom.setDisabled(disabled);
        // this.maxPixelRadiusControlDom.setDisabled(disabled);
        this.loopControlDom.setDisabled(disabled);
        this.gaussianViewElement.classList.toggle('hidden', disabled);
    }
    setRecordingCameraDomsDisabled(disabled: boolean) {
        this.modelPanelMode = disabled ? 'model' : 'recordingCamera';
        const titleKey = disabled ? 'title.modelSettings' : 'title.recordingCameraSettings';
        this.titledomElement.textContent = getI18nText(titleKey);
        // this.titledomElement.textContent = disabled ? '模型设置' : '录制相机设置';
        this.fovControlDom.setDisabled(disabled);
        this.nearPlaneControlDom.setDisabled(disabled);
        this.farPlaneControlDom.setDisabled(disabled);
        this.recordingCameraViewElement.classList.toggle('hidden', disabled);
    }
    setActive(active: boolean) {
        this.isActive = active;
    }

    /**
     * 释放所有资源，移除事件监听器
     */
    dispose(): void {
        console.log('disposing ModelPanelTools:', this.panelId);

        // 移除所有window事件监听器
        if (this.modelSelectChangeHandler) {
            window.removeEventListener('event.modelSelectChange', this.modelSelectChangeHandler);
            this.modelSelectChangeHandler = undefined;
        }

        if (this.gizmoObjectChangeHandler) {
            window.removeEventListener('event.gizmoObjectChange', this.gizmoObjectChangeHandler);
            this.gizmoObjectChangeHandler = undefined;
        }

        if (this.panelTabChangeHandler) {
            window.removeEventListener('event.panelTabChange', this.panelTabChangeHandler);
            this.panelTabChangeHandler = undefined;
        }

        if (this.modelParamsChangeHandler) {
            window.removeEventListener('event.modelParamsChange', this.modelParamsChangeHandler);
            this.modelParamsChangeHandler = undefined;
        }

        // 清理控制组件的引用（如果它们有dispose方法）
        // 注意：这些UI组件可能由BaseUITools管理，这里只清理引用
        this.posControlDom = null!;
        this.rotationControlDom = null!;
        this.scaleControlDom = null!;
        this.gaussianSizeControlDom = null!;
        this.gaussianOpacityControlDom = null!;
        this.sphericalLevelControlDom = null!;
        this.timeScaleControlDom = null!;
        this.timeOffsetControlDom = null!;
        this.maxPixelRadiusControlDom = null!;
        this.loopControlDom = null!;
        this.fovControlDom = null!;
        this.nearPlaneControlDom = null!;
        this.farPlaneControlDom = null!;

        // 清理DOM元素引用
        this.titledomElement = null!;
        this.recordingCameraViewElement = null!;
        this.gaussianViewElement = null!;

        // 重置状态
        this.isActive = false;
        this.modelPanelMode = 'model';
    }
}
